
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

interface ResultsFiltersProps {
  vacations: any[];
  filteredVacations: any[];
  isFiltered: boolean;
  onFilteredVacationsChange: (vacations: any[]) => void;
  onIsFilteredChange: (isFiltered: boolean) => void;
  onDisplayCountChange: (count: number) => void;
}

export const ResultsFilters = ({ 
  vacations, 
  filteredVacations, 
  isFiltered, 
  onFilteredVacationsChange, 
  onIsFilteredChange, 
  onDisplayCountChange 
}: ResultsFiltersProps) => {
  
  const handleLocalFilter = (refinement: string) => {
    console.log('Applying local filter:', refinement, 'to', vacations.length, 'vacations');
    const lowerRefinement = refinement.toLowerCase();
    
    // Always filter from the original vacations array, not the filtered one
    const filtered = vacations.filter(vacation => {
      const searchText = [
        vacation.name,
        vacation.description,
        vacation.descriptionLong,
        vacation.facilities,
        vacation.extraInfo,
        vacation.accommodationType,
        vacation.country,
        vacation.city,
        vacation.region,
        vacation.specialCategories,
        vacation.typeVakantie,
        vacation.typeAccommodatie
      ].join(' ').toLowerCase();

      // Handle specific filters with more comprehensive matching
      if (lowerRefinement.includes('zwembad') || lowerRefinement.includes('pool')) {
        return vacation.swimmingPool || 
               searchText.includes('zwembad') || 
               searchText.includes('pool') ||
               (vacation.facilities && vacation.facilities.toLowerCase().includes('zwembad'));
      }
      
      if (lowerRefinement.includes('all inclusive') || lowerRefinement.includes('ai')) {
        return vacation.all_inclusive === 'true' || 
               searchText.includes('all inclusive') || 
               searchText.includes('all-inclusive') ||
               searchText.includes('allinclusive');
      }
      
      if (lowerRefinement.includes('wellness') || lowerRefinement.includes('spa')) {
        return searchText.includes('wellness') || 
               searchText.includes('spa') || 
               searchText.includes('beauty') ||
               searchText.includes('sauna') ||
               searchText.includes('massage');
      }
      
      if (lowerRefinement.includes('familie') || lowerRefinement.includes('kinderen') || lowerRefinement.includes('families')) {
        return vacation.childFriendly === 'true' || 
               searchText.includes('familie') || 
               searchText.includes('kinderen') ||
               searchText.includes('families') ||
               searchText.includes('kids') ||
               searchText.includes('gezin');
      }

      if (lowerRefinement.includes('adults only') || lowerRefinement.includes('volwassenen')) {
        return searchText.includes('adults only') || 
               searchText.includes('volwassenen') ||
               searchText.includes('18+');
      }

      if (lowerRefinement.includes('restaurant') || lowerRefinement.includes('eten')) {
        return searchText.includes('restaurant') || 
               searchText.includes('dining') ||
               searchText.includes('eten') ||
               (vacation.facilities && vacation.facilities.toLowerCase().includes('restaurant'));
      }

      if (lowerRefinement.includes('bar') || lowerRefinement.includes('drankje')) {
        return searchText.includes('bar') || 
               searchText.includes('lounge') ||
               (vacation.facilities && vacation.facilities.toLowerCase().includes('bar'));
      }
      
      // Enhanced price filters - recognize both with and without euro symbol
      if (lowerRefinement.includes('€') || 
          lowerRefinement.includes('maximaal') || 
          lowerRefinement.includes('max') || 
          lowerRefinement.includes('onder') || 
          lowerRefinement.includes('tot') ||
          lowerRefinement.includes('budget') ||
          /\b\d+\s*(euro|eur|pp|per persoon)\b/.test(lowerRefinement)) {
        
        // Extract price from various formats
        let maxPrice = null;
        
        // Match patterns like "€600", "600€", "600 euro", "maximaal 600", "max 600", "onder 600", "600 pp"
        const pricePatterns = [
          /€(\d+)/,
          /(\d+)€/,
          /(\d+)\s*(euro|eur)/,
          /(maximaal|max|onder|tot|budget)\s*€?(\d+)/,
          /(\d+)\s*(pp|per persoon)/,
          /\b(\d+)\b(?=\s*$|[^\d€])/  // standalone number at end or followed by non-digit/euro
        ];
        
        for (const pattern of pricePatterns) {
          const match = lowerRefinement.match(pattern);
          if (match) {
            // For patterns with capturing groups, get the number
            maxPrice = parseInt(match[2] || match[1]);
            break;
          }
        }
        
        if (maxPrice && vacation.price && vacation.price > 0) {
          return vacation.price <= maxPrice;
        }
      }
      
      // General text search in all fields
      return searchText.includes(lowerRefinement);
    });

    console.log('Filter results:', filtered.length, 'vacations found');
    onFilteredVacationsChange(filtered);
    onIsFilteredChange(true);
    onDisplayCountChange(20); // Reset display count
    
    if (filtered.length > 0) {
      toast.success(`${filtered.length} resultaten gevonden voor "${refinement}"`);
    } else {
      toast.error(`Geen resultaten gevonden voor "${refinement}"`);
    }
  };

  const resetFilters = () => {
    console.log('Resetting filters, returning to', vacations.length, 'original vacations');
    onFilteredVacationsChange(vacations);
    onIsFilteredChange(false);
    onDisplayCountChange(20);
    toast.success("Filters weggehaald");
  };

  return {
    handleLocalFilter,
    resetFilters
  };
};
